import { create } from 'zustand';

export interface ConfirmOptions {
  title: string;
  description: string;
  /** Render description as trusted HTML (i18n strings only). */
  html?: boolean;
  /** Primary action. Set to false to hide. Default: { label: 'OK' }
   *  `disabled` greys the button out — e.g. to mirror a form's Save-enabled
   *  state; `disabledTooltip` explains why (shown on hover while disabled). */
  confirm?:
    | false
    | {
        label?: string;
        variant?: 'default' | 'destructive';
        disabled?: boolean;
        disabledTooltip?: string;
      };
  /** Cancel button. Set to false to hide. Default: { label: 'Cancel' } */
  cancel?: false | { label?: string };
  /** Optional alternative action (3rd button). Resolves with 'alt'. */
  alt?: { label: string; variant?: 'default' | 'destructive' };
}

/** Resolve value: 'confirm' | 'alt' | false (cancel/dismiss) */
export type ConfirmResult = 'confirm' | 'alt' | false;

interface ConfirmState {
  isOpen: boolean;
  options: ConfirmOptions | null;
  resolve: ((value: ConfirmResult) => void) | null;

  open: (options: ConfirmOptions, resolve: (value: ConfirmResult) => void) => void;
  close: (result: ConfirmResult) => void;
}

export const useConfirmStore = create<ConfirmState>((set, get) => ({
  isOpen: false,
  options: null,
  resolve: null,

  open: (options, resolve) => set({ isOpen: true, options, resolve }),
  close: (result) => {
    const { resolve } = get();
    resolve?.(result);
    set({ isOpen: false, options: null, resolve: null });
  },
}));

/**
 * Imperative confirm dialog — scio3 equivalent of scio1's `confirmService.confirm()`.
 *
 * ```ts
 * const confirm = useConfirm();
 *
 * // Simple
 * if (!(await confirm({ title: 'Delete', description: 'Are you sure?' }))) return;
 *
 * // Destructive, custom labels
 * await confirm({
 *   title: 'Delete',
 *   description: 'This cannot be undone.',
 *   confirm: { label: 'Delete', variant: 'destructive' },
 * });
 *
 * // Info-only (no cancel)
 * await confirm({ title: 'Notice', description: msg, cancel: false });
 *
 * // 3 buttons (confirm / alt / cancel)
 * const result = await confirm({
 *   title: 'MDM Control',
 *   description: 'Turn MDM on or off?',
 *   confirm: { label: 'Off', variant: 'destructive' },
 *   alt: { label: 'On' },
 * });
 * if (result === 'confirm') { // off }
 * if (result === 'alt') { // on }
 * ```
 */
export function useConfirm() {
  const open = useConfirmStore((s) => s.open);

  return (options: ConfirmOptions): Promise<ConfirmResult> =>
    new Promise((resolve) => open(options, resolve));
}
